import React, { useRef, useState, useEffect, useCallback } from 'react';
import Swiper from 'swiper';
import { landscapingContent } from './Landscaping.content';
import { Button } from '../ui/Button';
import arrow from '../HalfSlider/assets/arrow.svg';
import cloud1 from './assets/cloud.webp';
import cloud2 from './assets/cloud-2.webp';
import cloud3 from './assets/cloud-3.webp';
import logo from '../Feature/assets/logo.svg';
import styles from './Landscaping.module.sass';
import 'swiper/dist/css/swiper.min.css';

export function Landscaping() {
  const { kicker, title, subtitle, cardTitle, tabs } = landscapingContent;
  const [activeTab, setActiveTab] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [initialSlide, setInitialSlide] = useState(0);
  const [isContentAnimating, setIsContentAnimating] = useState(false);
  const containerRef = useRef(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.destroy(true, true);
      swiperRef.current = null;
    }
    if (!containerRef.current) return;

    setActiveSlide(initialSlide);

    swiperRef.current = new Swiper(containerRef.current, {
      slidesPerView: 1,
      speed: 700,
      initialSlide: initialSlide,
      on: {
        slideChange() {
          setActiveSlide(this.realIndex);
        },
      },
    });

    return () => {
      if (swiperRef.current) {
        swiperRef.current.destroy(true, true);
        swiperRef.current = null;
      }
    };
  }, [activeTab, initialSlide]);

  useEffect(() => {
    setIsContentAnimating(true);
    const timerId = window.setTimeout(() => {
      setIsContentAnimating(false);
    }, 700);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [activeTab, activeSlide]);

  const handleTabClick = useCallback((index) => {
    setInitialSlide(0);
    setActiveTab(index);
  }, []);

  const handlePrev = useCallback(() => {
    if (swiperRef.current) {
      const isFirstSlide = activeSlide === 0;
      const isFirstTab = activeTab === 0;

      if (isFirstSlide && isFirstTab) {
        // Переход на последний таб, последний слайд
        const lastTabIndex = tabs.length - 1;
        const lastSlideIndex = tabs[lastTabIndex].slides.length - 1;
        setInitialSlide(lastSlideIndex);
        setActiveTab(lastTabIndex);
      } else if (isFirstSlide) {
        // Переход на предыдущий таб, последний слайд
        const prevTabIndex = activeTab - 1;
        const lastSlideIndex = tabs[prevTabIndex].slides.length - 1;
        setInitialSlide(lastSlideIndex);
        setActiveTab(prevTabIndex);
      } else {
        swiperRef.current.slidePrev();
      }
    }
  }, [activeSlide, activeTab, tabs]);

  const handleNext = useCallback(() => {
    if (swiperRef.current) {
      const isLastSlide = activeSlide === tabs[activeTab].slides.length - 1;
      const isLastTab = activeTab === tabs.length - 1;

      if (isLastSlide && isLastTab) {
        // Переход на первый таб, первый слайд
        setInitialSlide(0);
        setActiveTab(0);
      } else if (isLastSlide) {
        // Переход на следующий таб
        setInitialSlide(0);
        setActiveTab(activeTab + 1);
      } else {
        swiperRef.current.slideNext();
      }
    }
  }, [activeSlide, activeTab, tabs]);

  const currentTab = tabs[activeTab];
  const currentSlide = currentTab.slides[activeSlide] || currentTab.slides[0];

  return (
    <section className={styles.land}>
      <div className={styles.land__clouds} aria-hidden>
        <img src={cloud1} alt="" className={styles['land__cloud--1']} />
        <img src={cloud2} alt="" className={styles['land__cloud--2']} />
        <img src={cloud3} alt="" className={styles['land__cloud--3']} />
      </div>

      <div className={styles.land__header}>
        <div className={styles.land__logo}>
          <img src={logo} alt="" />
        </div>
        <span className={styles.land__kicker}>{kicker}</span>
        <h2 className={styles.land__title}>
          {title.map((line, i) => (
            <React.Fragment key={i}>
              {line.accent ? (
                <span className={styles.land__titleAccent}>{line.text}</span>
              ) : (
                line.text
              )}
              {i < title.length - 1 && <br />}
            </React.Fragment>
          ))}
        </h2>
        <p className={styles.land__subtitle}>
          {subtitle.map((line, i) => (
            <React.Fragment key={i}>
              {line.accent ? (
                <span className={styles.land__subtitleAccent}>{line.text}</span>
              ) : (
                line.text
              )}
              {i < subtitle.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      </div>

      <div className={styles.land__body}>
        <div className={styles.land__sidebar}>
          <div className={styles.land__desc}>
            {cardTitle.map((line, i) => (
              <React.Fragment key={i}>
                {line.accent ? (
                  <span className={styles.land__descAccent}>{line.text}</span>
                ) : (
                  line.text
                )}
              </React.Fragment>
            ))}
          </div>

          <div className={styles.land__tabs}>
            {tabs.map((tab, i) => (
              <Button
                key={i}
                variant={i === activeTab ? 'simple' : 'glass'}
                className={`${styles.land__tabItem} ${i === activeTab ? styles['land__tabItem--active'] : ''}`}
                onClick={() => handleTabClick(i)}
                ariaLabel={tab.name}
              >
                {tab.name}
              </Button>
            ))}
          </div>

          <div className={styles.land__nav}>
            <button
              className={styles.land__navBtn}
              onClick={handlePrev}
              aria-label="Предыдущий слайд"
            >
              <img src={arrow} alt="" className={styles.land__navIcon} />
            </button>
            <button
              className={styles.land__navBtn}
              onClick={handleNext}
              aria-label="Следующий слайд"
            >
              <img
                src={arrow}
                alt=""
                className={`${styles.land__navIcon} ${styles['land__navIcon--right']}`}
              />
            </button>
          </div>

          <div
            className={`${styles.land__bottom} ${isContentAnimating ? styles['land__contentAnimated'] : ''}`}
          >
            <h3 className={styles.land__slideTitle}>{currentSlide.subtitle}</h3>
            <p className={styles.land__slideText}>
              {currentSlide.text.map((line, i) => (
                <React.Fragment key={i}>
                  {line.accent ? (
                    <span className={styles.land__slideTextAccent}>{line.text}</span>
                  ) : (
                    line.text
                  )}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>

        <div className={`${styles.land__slider} ${isContentAnimating ? styles['land__contentAnimated'] : ''}`}>
          <div
            className={`swiper-container ${styles.land__swiper}`}
            ref={containerRef}
            key={activeTab}
          >
            <div className="swiper-wrapper">
              {currentTab.slides.map((slide, i) => (
                <div key={i} className="swiper-slide">
                  <img src={slide.image} alt="" className={styles.land__image} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
